import type { FileObject } from '@supabase/storage-js';
import { supabase } from '@/lib/supabaseClient';
import { ensureAuthenticatedSession } from '@/utils/authentication';
import { getSignedDocumentUrl, uploadCommunityDocument } from '@/services/documentService';
import { getErrorStatus } from '@/utils/errors';
import { isRecord } from '@/utils/typeGuards';

const BUCKET_NAME = 'barrio-hub-manager';
const INCIDENT_ATTACHMENTS_CATEGORY = 'private/incidents';

const sanitizePathSegment = (segment: string) => {
  const trimmed = `${segment ?? ''}`
    .trim()
    .replace(/^[/\\.]+/, '')
    .replace(/[/\\]+$/, '');
  const withoutTraversal = trimmed.replace(/\.\.+/g, '-');
  return withoutTraversal;
};

const buildIncidentCategory = (incidentId: string) => {
  const safeIncidentId = sanitizePathSegment(incidentId);
  if (!safeIncidentId) {
    throw new Error('Identificador de incidencia inválido para adjuntos.');
  }
  return `${INCIDENT_ATTACHMENTS_CATEGORY}/${safeIncidentId}`;
};

const buildIncidentPath = (communityId: string, incidentId: string) => {
  const safeCommunityId = sanitizePathSegment(communityId);
  if (!safeCommunityId) {
    throw new Error('Identificador de comunidad inválido para adjuntos.');
  }
  return `${safeCommunityId}/${buildIncidentCategory(incidentId)}`;
};

const isImageMimeType = (mimeType?: string | null) =>
  typeof mimeType === 'string' && mimeType.startsWith('image/');

const getFileMetadata = (
  file: FileObject,
): { size: number; mimetype: string | null } => {
  const metadata = isRecord(file.metadata) ? file.metadata : null;
  const size = typeof metadata?.size === 'number' ? metadata.size : 0;
  const mimetype = typeof metadata?.mimetype === 'string' ? metadata.mimetype : null;
  return { size, mimetype };
};

export interface IncidentAttachment {
  name: string;
  path: string;
  mimeType: string | null;
  sizeInBytes: number;
  signedUrl: string;
  isImage: boolean;
}

export const uploadIncidentAttachment = async (
  communityId: string,
  incidentId: string,
  file: File
) => {
  const category = buildIncidentCategory(incidentId);
  await uploadCommunityDocument(communityId, category, file, { allowPrivate: true });
};

export const listIncidentAttachments = async (
  communityId: string,
  incidentId: string
): Promise<IncidentAttachment[]> => {
  await ensureAuthenticatedSession();
  const directory = buildIncidentPath(communityId, incidentId);
  const { data, error } = await supabase.storage.from(BUCKET_NAME).list(directory, {
    limit: 2,
    sortBy: { column: 'name', order: 'asc' },
  });

  if (error) {
    const status = getErrorStatus(error);
    if (status === 404) {
      return [];
    }
    throw new Error(error.message);
  }

  const files = (data ?? []).filter(item => item && item.name && (item.metadata || item.id));
  const attachments = await Promise.all(
    files.map(async (file: FileObject) => {
      const path = `${directory}/${file.name}`;
      const metadata = getFileMetadata(file);
      const mimeType = metadata.mimetype;
      try {
        const signedUrl = await getSignedDocumentUrl(path);
        return {
          name: file.name,
          path,
          mimeType,
          sizeInBytes: metadata.size,
          signedUrl,
          isImage: isImageMimeType(mimeType),
        };
      } catch (signedError) {
        console.warn('No se pudo generar la URL del adjunto de la incidencia.', signedError);
        return null;
      }
    })
  );

  return attachments.filter((attachment): attachment is IncidentAttachment => attachment !== null);
};

export const hasIncidentAttachments = async (
  communityId: string,
  incidentId: string
): Promise<boolean> => {
  await ensureAuthenticatedSession();
  const directory = buildIncidentPath(communityId, incidentId);
  const { data, error } = await supabase.storage.from(BUCKET_NAME).list(directory, {
    limit: 1,
    sortBy: { column: 'name', order: 'asc' },
  });

  if (error) {
    const status = getErrorStatus(error);
    if (status === 404) {
      return false;
    }
    throw new Error(error.message);
  }

  return (data ?? []).some(item => item && item.name);
};

export const deleteIncidentAttachmentsStorage = async (
  communityId: string,
  incidentId: string
): Promise<void> => {
  if (!communityId || !incidentId) return;

  try {
    await ensureAuthenticatedSession();
    const directory = buildIncidentPath(communityId, incidentId);

    // 1. Intentar listar los archivos del directorio de la incidencia en Storage
    const listResult = await supabase.storage.from(BUCKET_NAME).list(directory);
    const files = listResult.data;

    if (files && files.length > 0) {
      const filePaths = files.map(file => `${directory}/${file.name}`);
      const removeResult = await supabase.storage.from(BUCKET_NAME).remove(filePaths);
      if (removeResult.error) {
        console.warn('No se pudieron eliminar algunos adjuntos de Storage de la incidencia:', removeResult.error);
      }
    }

    // 2. Limpiar cualquier registro de document_metadata asociado
    const pathPrefix = `${directory}/`;
    const metadataResult = await supabase
      .from('document_metadata')
      .delete()
      .eq('community_id', communityId)
      .like('path', `${pathPrefix}%`);

    if (metadataResult.error) {
      console.warn('No se pudieron borrar los metadatos de documentos asociados:', metadataResult.error);
    }
  } catch (error) {
    console.warn('Advertencia no bloqueante al limpiar adjuntos de almacenamiento:', error);
  }
};
